import { useRouter } from "next/router";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";

type LegacyDashboardContextType = {
  displayAsLegacy: boolean;
};

const LegacyDashboardContext = createContext<LegacyDashboardContextType | null>(
  null
);

/**
 * Context provider for handling transition of
 * dashboard pages from the AngularJS web-app to NextJS.
 * When isLegacy is set in the browser URL we hide the
 * navigation items from the dashboard and only display
 * the page itself so that we can display the page in an
 * iframe on AngularJS.
 * This will be in effect for the dashboard until all the
 * navigation items have been built out on NextJS and we can
 * use nginx routing to handle the transition.
 */
export const LegacyDashboardProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const router = useRouter();

  // Initialize state by checking if classes already exist (added by inline script)
  // This prevents the useEffect from removing the class and causing a flash
  const [displayAsLegacy, setDisplayAsLegacy] = useState(() => {
    if (typeof document !== "undefined") {
      return document.documentElement.classList.contains("legacy-mode");
    }
    return false;
  });

  // Sync with router.query when it's ready
  useEffect(() => {
    if (router?.isReady && router.query?.isLegacy !== undefined) {
      setDisplayAsLegacy(router.query.isLegacy === "true");
    }
  }, [router?.isReady, router?.query?.isLegacy]);

  // Apply legacy-mode class to document element when in legacy mode
  useEffect(() => {
    if (typeof document !== "undefined") {
      if (displayAsLegacy) {
        document.documentElement.classList.add("legacy-mode");
      } else {
        document.documentElement.classList.remove("legacy-mode");
      }
    }
  }, [displayAsLegacy]);

  return (
    <LegacyDashboardContext.Provider value={{ displayAsLegacy }}>
      {children}
    </LegacyDashboardContext.Provider>
  );
};

/**
 */
export const useLegacyDashboard = (): LegacyDashboardContextType => {
  const context = useContext(LegacyDashboardContext);

  if (context === null) {
    throw new Error(
      "useLegacyDashboard must be used within a LegacyDashboardProvider"
    );
  }

  return context;
};
